import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
  upsertOrGroup,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

const STATUS_OPTIONS = [
  { value: "all", label: "ສະຖານະທັງໝົດ" },
  { value: "active", label: "ໃຊ້ງານປົກກະຕິ" },
  { value: "suspended", label: "ລະງັບ" },
  { value: "unverified", label: "ລໍຖ້າຢືນຢັນ" },
] as const;

const VERIFIED_OPTIONS = [
  { value: "all", label: "ລະດັບການຢືນຢັນທັງໝົດ" },
  { value: "none", label: "ຍັງບໍ່ຢືນຢັນ" },
  { value: "basic", label: "ພື້ນຖານ" },
  { value: "verified", label: "ຢືນຢັນແລ້ວ" },
] as const;

export function ShopsFilter() {
  const nav = useNavigate({ from: "/app/shops" });
  const search = useSearch({ from: "/app/shops" }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const searchFilter = findCondition(filters, "name");
  const searchPhoneFilter = findCondition(filters, "phone");
  const statusFilter = findCondition(filters, "status");
  const verifiedFilter = findCondition(filters, "verifiedLevel");

  const [searchValue, setSearchValue] = useState<string>(
    (searchFilter?.value as string) ||
      (searchPhoneFilter?.value as string) ||
      "",
  );

  useEffect(() => {
    const nameVal = searchFilter?.value as string;
    const phoneVal = searchPhoneFilter?.value as string;
    setSearchValue(nameVal || phoneVal || "");
  }, [searchFilter?.value, searchPhoneFilter?.value]);

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertOrGroup(filters, [
        { field: "name", op: "contains", value: val },
        { field: "phone", op: "contains", value: val },
      ]);
    } else {
      nextFilters = removeConditions(filters, "name");
      nextFilters = removeConditions(nextFilters, "phone");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const statusValue = (statusFilter?.value as string) || "all";
  const verifiedValue = (verifiedFilter?.value as string) || "all";
  const hasFilter =
    !!searchFilter?.value ||
    !!searchPhoneFilter?.value ||
    statusValue !== "all" ||
    verifiedValue !== "all";

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາຊື່ຫຼືເບີໂທ..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <SimpleSelect
            value={statusValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-40"
            options={STATUS_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
          />

          <SimpleSelect
            value={verifiedValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "verifiedLevel");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "verifiedLevel",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ລະດັບການຢືນຢັນ"
            className="h-8 w-full sm:w-40"
            options={VERIFIED_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({ search: { ...search, filters: undefined, offset: 0 } })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
